import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';
import { agentSessionsService, type AssignedSkillDTO } from '@/services/roros/agent-sessions.service';

type UseAgentSessionSlashAutocompleteArgs = {
  activeSessionId: string | undefined;
};

export function useAgentSessionSlashAutocomplete({
  activeSessionId,
}: UseAgentSessionSlashAutocompleteArgs) {
  const [inputText, setInputText] = useState('');
  const [skills, setSkills] = useState<AssignedSkillDTO[]>([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [skillQuery, setSkillQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // AC-AGS-6: load assigned skills for slash autocomplete when session changes
  useEffect(() => {
    if (!activeSessionId) return;
    agentSessionsService.listSkills(activeSessionId).then(setSkills).catch(() => setSkills([]));
  }, [activeSessionId]);

  const closeAutocomplete = useCallback(() => {
    setShowAutocomplete(false);
    setSkillQuery('');
  }, []);

  const clearInput = useCallback(() => {
    setInputText('');
    closeAutocomplete();
  }, [closeAutocomplete]);

  // AC-AGS-6: slash autocomplete opens while typing `/skill`
  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputText(val);

    if (val.startsWith('/')) {
      const query = val.slice(1);
      if (!query.includes(' ')) {
        setSkillQuery(query);
        setShowAutocomplete(true);
        return;
      }
    }

    closeAutocomplete();
  }, [closeAutocomplete]);

  const selectSkill = useCallback((name: string) => {
    setInputText(`/${name} `);
    closeAutocomplete();
    inputRef.current?.focus();
  }, [closeAutocomplete]);

  return {
    inputRef,
    inputText,
    skills,
    showAutocomplete,
    skillQuery,
    clearInput,
    closeAutocomplete,
    handleInputChange,
    selectSkill,
  };
}
